// Per spec/blocks.md §8. Photo-led event grid used for Big 3 / Signature
// Events and similar curated card lists.
//
// **P-037.22 / D-130 rework.** Banks's audit converged event-card-group
// with the events-list CTA contract + system-wide 3-col rhythm:
//
// - `options.style` (`big-3 | signature | generic`) and
//   `options.columns` (`2 | 3`) both dropped. Without the eyebrow
//   header (also dropped), `style` was vestigial; with the layout
//   pinned at 3-col + 190 × 100 fixed image, `columns` was vestigial
//   too. The form-side `big-3` fixed-slot UX (3 "Meeting" slots) drops
//   with `style`. Users compose a `section-heading` or
//   `sub-section-heading` Block above when they want a heading.
//   `optionsSchema = z.object({})`.
// - `inputs.eyebrow` dropped — EyebrowLabel Component deleted in the
//   same pass (event-card-group was its sole consumer).
// - Per-event schema flattened: `ctaLabel` constrained to the events-
//   list enum (`Register Now | Add to Waitlist | Save the Date`) +
//   `ctaUrl` independently optional. Save-the-Date renders text-only;
//   link-arm labels without a URL render as text fallback (mirror of
//   events-list `eventToProps` per D-115).
// - Render: 3-col `gridLayout(items, 3, ...)` inside a 30 px L/R + 30 px
//   top padded outer cell — matches events-list's outer shape (D-128)
//   and the system-wide 30/190/25/190/25/190/30 3-col rhythm (D-117).
//   Image dimensions land in EventCard at 190 × 100; the per-event
//   imageWidth override is gone.
//
// **P-037.23 / D-131 — image optional + fallback.** `imageUrl` is now
// optional on each event. When unset, EventCard renders the chapter
// fallback PNG (`/fallback-image.png`, 190 × 100, decorative `alt=""`).
// The 10 px navy bar above the image is unaffected; the inset shadow
// applies to both the staff-uploaded and fallback image.
//
// Auto-hide: orchestrator-level skip on `events.length === 0` per the
// shared `*.length === 0` convention.

import { z } from 'zod'
import { defineBlock } from '../render/define-block'
import { html } from '../render'
import { eventCard, type EventCardProps } from '../components/event-card'
import { gridLayout, pickDefined } from '../components/helpers'

// P-037.31 fourth post-Phase-Close §A — `'Learn More'` added per Banks's audit.
const CTA_LABEL_ENUM = ['Register Now', 'Add to Waitlist', 'Learn More', 'Save the Date'] as const
type CtaLabel = (typeof CTA_LABEL_ENUM)[number]

const eventCardItemSchema = z.object({
  title: z.string().min(1),
  dateText: z.string().min(1),
  location: z.string().min(1).optional(),
  // P-037.23 D-131: imageUrl is now optional. When unset, EventCard
  // renders the chapter fallback PNG. The `.describe('imageUrl')` brand
  // keeps `<ImageUrlField>` (vs. plain `<UrlField>`) as the form-side
  // dispatch — staff can still upload / browse / paste a URL or leave
  // the field empty for the fallback.
  imageUrl: z.string().url().describe('imageUrl').optional(),
  ctaLabel: z.enum(CTA_LABEL_ENUM).optional(),
  ctaUrl: z.string().url().optional(),
})

type EventCardItem = z.infer<typeof eventCardItemSchema>

const optionsSchema = z.object({})

const inputsSchema = z.object({
  events: z.array(eventCardItemSchema).max(6),
})

function eventToProps(item: EventCardItem, linkColor: string): EventCardProps {
  // P-037.23 D-131: imageUrl optional. `pickDefined` strips it from the
  // EventCard props when unset, so EventCard sees `imageUrl: undefined`
  // (the `.optional()` arm) and renders the fallback PNG.
  const baseProps = {
    title: item.title,
    dateText: item.dateText,
    linkColor,
    ...pickDefined({ location: item.location, imageUrl: item.imageUrl }),
  }
  if (item.ctaLabel === undefined) {
    return baseProps
  }
  // Save the Date → text-only (ignore ctaUrl). Register Now / Add to
  // Waitlist + ctaUrl → link arm. Label set but no URL on a link-arm
  // label → text fallback. EventCard dispatches text-vs-link by
  // ctaUrl presence, so we only forward ctaUrl when it's relevant.
  const isLinkArm = item.ctaLabel !== 'Save the Date' && item.ctaUrl !== undefined
  return isLinkArm
    ? { ...baseProps, ctaLabel: item.ctaLabel, ctaUrl: item.ctaUrl as string }
    : { ...baseProps, ctaLabel: item.ctaLabel }
}

export const eventCardGroup = defineBlock({
  id: 'event-card-group',
  label: 'Event Card Group',
  category: 'content',
  optionsSchema,
  inputsSchema,
  defaultOptions: {},
  defaultInputs: { events: [] },
  autoHide: ({ inputs }) => inputs.events.length === 0,
  render: ({ inputs }, ctx) => {
    const grid = gridLayout(inputs.events, 3, (e) => eventCard(eventToProps(e, ctx.linkColor)))
    // 30 px L/R padded outer cell — matches events-list's outer
    // shape. Top/bottom padding lives on the system-level inter-
    // Block spacer (`src/render/block-padding.ts`) per P-037.29
    // D-137 §item 2.
    // prettier-ignore
    return html`<table role="presentation" cellpadding="0" cellspacing="0" border="0" width="100%"><tr><td style="padding: 0 30px;">${grid}</td></tr></table>`
  },
})

// Re-exports for tests
export type { CtaLabel }
